import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getUserSurvey } from "../../api/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import SignedOutPrompt from "../SignedOutPrompt";
import SurveyAnswersReadOnly from "../survey/SurveyAnswersReadOnly";
import { formatSubmittedAt } from "../survey/surveyUtils";

/**
 * Survey intro, CTA (take / continue / edit), and previous submissions for retakes.
 *
 * @param {Object} props
 * @param {number} [props.userId]
 * @param {object} [props.user]
 * @returns {JSX.Element}
 */
export default function SurveyStartPage({ userId, user }) {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openHistoryId, setOpenHistoryId] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!userId || !surveyId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      const result = await getUserSurvey(userId, surveyId);
      if (!mounted) return;
      if (result.error) {
        setError(result.error);
        setDetail(null);
      } else {
        setDetail(result.data);
      }
      setLoading(false);
    }
    load();
    return () => {
      mounted = false;
    };
  }, [userId, surveyId]);

  if (!userId) {
    return (
      <SignedOutPrompt
        title="Beta feedback"
        description="Sign in to complete this survey."
      />
    );
  }

  if (user?.type === "organization") {
    return (
      <SignedOutPrompt
        variant="restricted"
        title="Beta feedback"
        description="Beta surveys are available on personal accounts."
      />
    );
  }

  const definition = detail?.definition;
  const draft = detail?.draft;
  const submitted = detail?.submitted;
  const history = detail?.history || [];
  const allowEdit = Boolean(definition?.allowEdit);
  const allowRetake = Boolean(definition?.allowRetake);

  let ctaLabel = "Start Survey";
  let showCta = true;
  if (!allowRetake && submitted && allowEdit && !draft) {
    ctaLabel = "Edit answers";
  } else if (!allowRetake && submitted && !allowEdit) {
    showCta = false;
  }

  return (
    <div className="bg-ui-background min-h-[calc(100dvh-3.5rem)] w-full">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-10">
        <Link to="/beta" className="text-sm text-accent hover:text-accent-hover">
          ← All surveys
        </Link>

        {loading ? <p className="mt-8 text-sm text-text-muted">Loading survey…</p> : null}
        {error ? (
          <p className="mt-6 text-sm text-error" role="alert">
            {error}
          </p>
        ) : null}

        {definition ? (
          <>
            <div className="mt-6 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-display text-3xl font-medium text-text-primary">
                  {definition.title}
                </h1>
                {definition.estimatedMinutes ? (
                  <p className="mt-1 text-sm text-text-muted">
                    About {definition.estimatedMinutes} minutes
                  </p>
                ) : null}
              </div>
              {!allowRetake && submitted ? <Badge variant="accent">Completed</Badge> : null}
            </div>

            <Card className="mt-6 border-ui-border">
              <CardHeader>
                <CardTitle className="text-xl">{definition.intro?.title}</CardTitle>
                <CardDescription className="whitespace-pre-wrap text-base text-text-secondary">
                  {definition.intro?.body}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex justify-center">
                {showCta ? (
                  <Button type="button" size="lg" onClick={() => navigate(`/beta/${surveyId}/take`)}>
                    {ctaLabel}
                  </Button>
                ) : (
                  <p className="text-sm text-text-secondary">You have already submitted this survey.</p>
                )}
              </CardContent>
            </Card>

            {allowRetake && history.length > 0 ? (
              <section className="mt-10">
                <h2 className="text-lg font-semibold text-text-primary">Previous submissions</h2>
                <ul className="mt-4 flex flex-col gap-3">
                  {history.map((row, index) => {
                    const open = openHistoryId === row.id;
                    return (
                      <li key={row.id}>
                        <Card className="border-ui-border">
                          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 py-4">
                            <div>
                              <CardTitle className="text-base">
                                Submission {history.length - index}
                              </CardTitle>
                              <CardDescription>
                                {formatSubmittedAt(row.submitted_at)}
                              </CardDescription>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setOpenHistoryId(open ? null : row.id)}
                            >
                              {open ? "Hide" : "View"}
                            </Button>
                          </CardHeader>
                          {open ? (
                            <CardContent className="border-t border-ui-border pt-6">
                              <SurveyAnswersReadOnly
                                definition={definition}
                                answers={row.answers || {}}
                              />
                            </CardContent>
                          ) : null}
                        </Card>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
